import { Component, type ReactNode } from "react";
import { motion } from "framer-motion";
import { WarningCircle, ArrowClockwise } from "@phosphor-icons/react";

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full gap-4 p-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
            <WarningCircle size={24} weight="bold" className="text-[var(--color-soft-red)]" />
          </div>
          <div className="text-center max-w-sm">
            <p className="text-[13px] font-medium text-[var(--text-primary)]">
              {this.props.fallbackMessage || "Something went wrong"}
            </p>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">
              {this.state.error?.message || "An unexpected error occurred."}
            </p>
          </div>
          <motion.button
            onClick={() => { this.setState({ hasError: false, error: null }); }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[11px] font-medium bg-[var(--text-primary)] text-[var(--text-inverse)] hover:opacity-90 transition-opacity duration-100"
          >
            <ArrowClockwise size={12} weight="bold" />
            Retry
          </motion.button>
        </div>
      );
    }

    return this.props.children;
  }
}
